import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import {
  CheckCircleOutline as CheckCircleIcon,
  AppRegistration,
  TableRestaurant,
} from "@mui/icons-material";
import apiClient from "../../api/apiClient";
import { useSnackbar } from "../../context/SnackbarProvider";

const Registration = () => {
  const { eventslug } = useParams();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const [eventType, setEventType] = useState(null);

  // Common state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // employee state
  const [employeeId, setEmployeeId] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);

  // public state
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    company: "",
  });

  useEffect(() => {
    if (!eventslug) {
      showSnackbar("Event slug is missing", "error");
      navigate("/");
      return;
    }

    const fetchEventType = async () => {
      try {
        const response = await apiClient.get(`/events/slug/${eventslug}`);
        if (!response.data) {
          throw new Error("Event not found");
        }
        setEventType(response.data.eventType);
      } catch (err) {
        showSnackbar(
          err.response?.data?.message || "Failed to fetch event details",
          "error"
        );
        navigate("/");
      }
    };
    fetchEventType();
  }, [eventslug, navigate, showSnackbar]);

  // Common handlers
  const handleDialogClose = () => {
    setShowDialog(false);
    navigate(`/e/${eventslug}`);
  };

  // employee handlers
  const handleEmployeeIdChange = (e) => {
    setEmployeeId(e.target.value);
  };

  // public handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && !/^\+?[0-9]*$/.test(value)) return;
    setFormData({ ...formData, [name]: value });
    setError(null);
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // employee submission
  const handleemployeeSubmit = async () => {
    if (!employeeId) {
      showSnackbar("Please enter your Employee ID.", "error");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post("/registrations", {
        employeeId,
        slug: eventslug,
      });

      if (response.data.success) {
        const { employeeName, tableNumber, tableImage } = response.data.data;
        const confirmationMessage = `${employeeName}, your table number is ${tableNumber}.`;
        setConfirmationData({ message: confirmationMessage, tableImage });
        setShowDialog(true);
        setEmployeeId("");
      } else {
        showSnackbar(response.data.message || "Failed to register.", "error");
      }
    } catch (err) {
      if (err.response?.status === 409) {
        showSnackbar("You are already registered for this event", "error");
      } else {
        showSnackbar(
          err.response?.data?.message || "Failed to register.",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // public submission
  const handlepublicSubmit = async () => {
    if (!formData.fullName || !formData.phone || !formData.email) {
      setError("Please fill all the required fields.");
      return;
    }

    if (!isValidEmail(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post("/registrations", {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        slug: eventslug,
      });

      if (response.data.success) {
        setShowDialog(true);
        setFormData({
          fullName: "",
          phone: "",
          email: "",
          company: "",
        });
      } else {
        showSnackbar(response.data.message || "Failed to register.", "error");
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setError("You are already registered with this email or phone.");
      } else {
        setError(err.response?.data?.message || "Failed to register.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!eventType) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "calc(100vh - 120px)",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const isPublicEvent = eventType === "public";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "calc(100vh - 120px)",
        backgroundColor: "#f5f5f5",
        padding: "20px",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: "30px",
          maxWidth: "600px",
          width: "100%",
          borderRadius: "12px",
          textAlign: "center",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Header with mode-specific title and icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          {isPublicEvent ? (
            <AppRegistration
              sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
            />
          ) : (
            <TableRestaurant
              sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
            />
          )}
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'Comfortaa', cursive",
              fontWeight: "bold",
              color: "#333",
            }}
          >
            {isPublicEvent ? "Register for the Event" : "Get Your Table"}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ marginBottom: "20px" }}>
            {error}
          </Alert>
        )}

        {/* employee Form */}
        {!isPublicEvent && (
          <>
            <TextField
              fullWidth
              name="employeeId"
              label="Employee ID"
              type="number"
              value={employeeId}
              onChange={handleEmployeeIdChange}
              sx={{ marginBottom: "20px" }}
            />

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleemployeeSubmit}
              disabled={loading}
              sx={{
                fontSize: { xs: "14px", md: "16px" },
                padding: "10px",
                textTransform: "none",
                borderRadius: "8px",
                maxWidth: { sm: "300px" },
                "&:hover": {
                  background: "secondary.main",
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : "Submit"}
            </Button>
          </>
        )}

        {/* public Form */}
        {isPublicEvent && (
          <>
            <TextField
              fullWidth
              name="fullName"
              label="Full Name"
              value={formData.fullName}
              onChange={handleInputChange}
              sx={{ marginBottom: "20px" }}
            />
            <TextField
              fullWidth
              name="phone"
              label="Phone Number (with country code)"
              value={formData.phone}
              onChange={handleInputChange}
              sx={{ marginBottom: "20px" }}
            />
            <TextField
              fullWidth
              name="email"
              label="Email"
              value={formData.email}
              onChange={handleInputChange}
              error={formData.email && !isValidEmail(formData.email)}
              helperText={
                formData.email && !isValidEmail(formData.email)
                  ? "Invalid email format"
                  : ""
              }
              sx={{ marginBottom: "20px" }}
            />
            <TextField
              fullWidth
              name="company"
              label="Company (Optional)"
              value={formData.company}
              onChange={handleInputChange}
              sx={{ marginBottom: "20px" }}
            />

            <Button
              variant="contained"
              fullWidth
              onClick={handlepublicSubmit}
              disabled={loading}
              sx={{
                fontSize: "16px",
                padding: "10px 20px",
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": {
                  background: "secondary.main",
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : "Submit"}
            </Button>
          </>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={showDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
            padding: "16px",
            width: "90%",
            maxWidth: "650px",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", paddingBottom: "10px" }}>
          <CheckCircleIcon
            sx={{
              fontSize: "100px",
              color: "#28a745",
              marginBottom: "20px",
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "28px", md: "36px" },
              color: "linear-gradient(90deg, #FF6F61 0%, #D72638 100%)",
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "1.5px",
              textShadow: "2px 2px 5px rgba(0,0,0,0.3)",
              animation: "fadeIn 1.2s ease-in-out",
              "@keyframes fadeIn": {
                "0%": { opacity: 0, transform: "translateY(-10px)" },
                "100%": { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            {isPublicEvent ? "Registration Successful!" : "Welcome!"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            sx={{
              fontSize: "18px",
              fontWeight: "500",
              color: "#555",
              marginBottom: "20px",
              textAlign: "center",
            }}
          >
            {isPublicEvent
              ? "Thank you for registering. We look forward to seeing you at the event!"
              : confirmationData?.message}
          </Typography>
          {!isPublicEvent && confirmationData?.tableImage && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <img
                src={confirmationData.tableImage}
                alt="Table"
                style={{
                  maxWidth: "100%",
                  maxHeight: "450px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                  objectFit: "cover",
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            onClick={handleDialogClose}
            color="primary"
            variant="contained"
            sx={{
              fontSize: "16px",
              padding: "10px 20px",
              width: "150px",
              textTransform: "none",
              borderRadius: "8px",
            }}
          >
            {isPublicEvent ? "View Event Details" : "Okay"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Registration;
